import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, Table, Spinner } from "react-bootstrap";

const BinanceMarket = () => {
  const [coinList, setCoinList] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("volume"); // 기본 정렬: 거래대금
  const [sortOrder, setSortOrder] = useState("desc"); // 기본 내림차순

  // USDT 마켓 코인 가져오기 (바이낸스)
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        const response = await axios.get("https://api.binance.com/api/v3/exchangeInfo");
        const binanceMarkets = response.data.symbols
          .filter((market) => market.quoteAsset === "USDT")
          .map((market) => market.symbol); // USDT 마켓만 필터링
        setCoinList(binanceMarkets);        
        setLoading(false);
      } catch (error) {
        console.error("코인 목록 가져오기 실패:", error);
      }
    };

    fetchMarketData();
  }, []);

  // 실시간 시세 업데이트 (바이낸스 - WebSocket 사용)
  useEffect(() => {
    if (coinList.length === 0) return;

    // WebSocket 연결을 위한 준비
    const wsList = coinList.map((symbol) => {
      const ws = new WebSocket(`wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@trade`);
      
      ws.onopen = () => {
        console.log(`웹소켓 연결 성공: ${symbol}`);
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const symbol = data.s; // Symbol 정보 (예: BTCUSDT)

        setPrices((prevPrices) => ({
          ...prevPrices,
          [symbol]: {
            name: symbol.replace("USDT", ""),
            price: parseFloat(data.p), // 거래 가격
            change: 0, // 가격 변화율은 WebSocket으로 바로 받기 어려우므로 나중에 처리할 수 있습니다.
            volume: parseFloat(data.q), // 거래량
          },
        }));
      };

      ws.onclose = () => {
        console.log(`웹소켓 연결 종료: ${symbol}`);
      };

      ws.onerror = (error) => {
        console.error(`웹소켓 오류: ${symbol}`, error);
      };

      return ws;
    });

    // 컴포넌트가 언마운트될 때 WebSocket 연결 종료
    return () => {
      wsList.forEach((ws) => ws.close());
    };
  }, [coinList]);

  // 정렬 아이콘 반환
  const getSortIcon = (key) => {
    if (sortBy !== key) return "";
    return sortOrder === "asc" ? "▲" : sortOrder === "desc" ? "▼" : "";
  };

  // 정렬 로직
  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === "asc" ? "desc" : sortOrder === "desc" ? "default" : "asc");
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
  };

  // 정렬 적용
  const sortedData = Object.values(prices).sort((a, b) => {
    if (sortOrder === "default") return 0; // 기본 상태 유지
    if (sortOrder === "asc") return a[sortBy] > b[sortBy] ? 1 : -1;
    if (sortOrder === "desc") return a[sortBy] < b[sortBy] ? 1 : -1;
    return 0;
  });

  return (
    <Card className="shadow-sm p-3 mb-4 bg-white rounded mx-auto" style={{ width: "100%", maxWidth: "450px" }}>
      <Card.Body>
        <Card.Title className="text-center fw-bold fs-5">바이낸스 실시간 USDT 마켓</Card.Title>

        {loading ? (
          <div className="text-center my-3">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table striped hover size="sm">
            <thead>
              <tr className="text-center">
                <th onClick={() => handleSort("name")} style={{ cursor: "pointer", textAlign: "left" }}>
                  {getSortIcon("name")} 코인명
                </th>
                <th onClick={() => handleSort("price")} style={{ cursor: "pointer", textAlign: "right" }}>
                  {getSortIcon("price")} 현재가
                </th>
                <th onClick={() => handleSort("change")} style={{ cursor: "pointer", textAlign: "right" }}>
                  {getSortIcon("change")} 전일대비
                </th>
                <th onClick={() => handleSort("volume")} style={{ cursor: "pointer", textAlign: "right" }}>
                  {getSortIcon("volume")} 거래대금
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((coin) => (
                <tr key={coin.name} className="text-center">
                  <td className="fw-bold" style={{ textAlign: "left" }}>{coin.name}</td>
                  <td style={{ textAlign: "right" }}>{coin.price?.toLocaleString() || "..."}</td>
                  <td
                    className={coin.change >= 0 ? "text-success fw-bold" : "text-danger fw-bold"}
                    style={{ textAlign: "right" }}
                  >
                    {coin.change > 0 ? `+${coin.change.toFixed(2)}%` : `${coin.change?.toFixed(2)}%`}
                  </td>
                  <td style={{ textAlign: "right" }}>{coin.volume.toLocaleString()} USDT</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export { BinanceMarket };
